import { useContext } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ChatRoute } from '../../Routes';
import { Store, StoreContextType } from '../context';
import { FeatureName, defaultFeatureFlagsState } from '../models/featureFlags';
import { GptTypes } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';

export const useConversations = () => {
    const location = useLocation();
    const { id: conversationId } = useParams();
    const context = useContext(Store) as StoreContextType;

    if (context === undefined) {
        throw new Error('useConversations must be used within a StoreProvider');
    }
    let activeChat = undefined;
    // If the current route is a chat route or the root, we want to set the active chat to the one with the corresponding conversationId
    // If nothing is found, we prioritize DRAFT conversation, then first SyGPT chat, then first chat in the list
    if (location.pathname.startsWith(ChatRoute) || location.pathname === '') {
        activeChat =
            context.state.chats.find((c) => c.id === conversationId) ||
            context.state.chats.find((c) => c.id === 'DRAFT') ||
            context.state.chats.find((c) => c.gpt?.type === GptTypes.SyGpt) ||
            context.state.chats[0];
    }

    let activeGpt = undefined;
    // If the active chat is set, we want to find the corresponding GPT configuration (it might not be SyGPT type)
    if (activeChat) {
        activeGpt =
            context.state.gptConfigurations.find((g) => g.id === activeChat.gpt?.id) ||
            context.state.gptConfigurations.find((g) => g.isDefault);
    }
    return {
        chats: context.state.chats,
        conversationsCursor: context.state.conversationsCursor,
        activeChat,
        activeGpt,
        dispatch: context.dispatch,
    };
};

export const useQuota = () => {
    const context = useContext(Store) as StoreContextType;

    if (context === undefined) {
        throw new Error('useQuota must be used within a StoreProvider');
    }

    return { quota: context.state.quota, dispatch: context.dispatch };
};

export const useRootState = () => {
    const context = useContext(Store) as StoreContextType;

    if (context === undefined) {
        throw new Error('useRootState must be used within a StoreProvider');
    }

    return context;
};

export const useTheme = () => {
    const context = useContext(Store) as StoreContextType;

    if (context === undefined) {
        throw new Error('useTheme must be used within a StoreProvider');
    }

    return { theme: context.state.theme, themeMode: context.state.themeMode, dispatch: context.dispatch };
};

export const useFeatures = () => {
    const context = useContext(Store) as StoreContextType;

    if (context === undefined) {
        throw new Error('useTheme must be used within a StoreProvider');
    }

    return { features: context.state.features, dispatch: context.dispatch };
};

export const useFeature = (feature: FeatureName) => {
    const context = useContext(Store) as StoreContextType;
    return context.state.features[feature] ?? defaultFeatureFlagsState[feature];
};

export const useUserPreferences = () => {
    const context = useContext(Store) as StoreContextType;

    if (context === undefined) {
        throw new Error('useUserPreferences must be used within a StoreProvider');
    }

    return { userPreferences: context.state.userPreferences, dispatch: context.dispatch };
};

export const useGptConfigurations = () => {
    const context = useContext(Store) as StoreContextType;

    if (context === undefined) {
        throw new Error('useGptConfigurations must be used within a StoreProvider');
    }

    return { gptConfigurations: context.state.gptConfigurations, dispatch: context.dispatch };
};

export const useSurvey = () => {
    const context = useContext(Store) as StoreContextType;

    if (context === undefined) {
        throw new Error('useSurvey must be used within a StoreProvider');
    }

    return { survey: context.state.survey, dispatch: context.dispatch };
};
